import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ContractAnalyzer } from "@/components/ContractAnalyzer";
import {
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  Lightbulb,
  Home as HomeIcon,
  ShieldCheck,
  ListChecks,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const TITLE = "Signing Your First Apartment Lease — A Renter's Survival Guide";
const META_TITLE = "Signing Your First Apartment Lease — legaldecoder";
const META_DESC =
  "First lease? Here's exactly what to check, the clauses that trap new renters, and how to spot red flags before you sign.";

const FAQ = [
  {
    q: "What's the most important clause to check in a first lease?",
    a: "The security deposit clause and the early termination clause. The deposit is your largest upfront payment; the termination clause decides what happens if you have to leave early. Both are commonly written in the landlord's favor.",
  },
  {
    q: "Can I negotiate a lease as a first-time renter?",
    a: "Yes. Landlords expect negotiation on lease length, deposit amount, pet fees, and renewal terms. The worst they can say is no — and many small concessions add up.",
  },
  {
    q: "What's a 'normal' security deposit?",
    a: "Most U.S. states cap deposits at 1–2 months' rent. Anything more is a red flag and may be illegal where you live.",
  },
  {
    q: "What happens if I sign and then change my mind?",
    a: "There is no general 'cooling-off' period for residential leases in most states. Once you sign, you're typically bound to the early termination clause — read it before signing, not after.",
  },
];

export const Route = createFileRoute("/scenarios/first-apartment-lease")({
  head: () => ({
    meta: [
      { title: META_TITLE },
      { name: "description", content: META_DESC },
      { property: "og:title", content: META_TITLE },
      { property: "og:description", content: META_DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://legaldecoder.io/scenarios/first-apartment-lease" },
    ],
    links: [{ rel: "canonical", href: "https://legaldecoder.io/scenarios/first-apartment-lease" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: FirstLeaseScenario,
});

function FirstLeaseScenario() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="relative overflow-hidden border-b border-border">
          <div className="absolute inset-0 bg-gradient-glow" aria-hidden />
          <div className="relative mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <HomeIcon className="h-3.5 w-3.5" /> First-time renter guide
            </span>
            <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">{TITLE}</h1>
            <p className="mt-4 text-lg text-muted-foreground">
              You found the apartment. The landlord just sent over a 14-page lease. Here&apos;s
              exactly what to read first, what to negotiate, and what to walk away from.
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
          <h2 className="flex items-center gap-2 text-2xl font-bold">
            <ListChecks className="h-6 w-6 text-primary-glow" />
            The 7 things to check before you sign
          </h2>
          <ol className="mt-6 space-y-4">
            {[
              {
                t: "Deposit amount and return rules",
                d: "Should be no more than 1–2 months' rent in most states, with a written return deadline.",
                href: "/clauses/security-deposit-clause",
                hrefLabel: "Security deposit clause →",
              },
              {
                t: "Lease term and renewal",
                d: "Find the exact end date, and watch for an auto-renewal clause that locks you in for another year if you forget to give notice.",
                href: "/clauses/auto-renewal-clause",
                hrefLabel: "Auto-renewal clause →",
              },
              {
                t: "Early termination cost",
                d: "What does it cost to break the lease? Aim for a flat fee (1–2 months) — not 'all remaining rent.'",
                href: "/clauses/early-termination-clause",
                hrefLabel: "Early termination clause →",
              },
              {
                t: "Late fees and grace period",
                d: "A 3–5 day grace period and a capped flat or % fee is normal. Daily compounding fees are not.",
                href: "/clauses/late-fees-clause",
                hrefLabel: "Late fees clause →",
              },
              {
                t: "Landlord entry rights",
                d: "Your landlord should give 24–48 hours written notice except in emergencies. 'Enter at any time' is not acceptable.",
                href: "/clauses/entry-notice-clause",
                hrefLabel: "Entry notice clause →",
              },
              {
                t: "Maintenance and repairs",
                d: "Confirm who pays for what. Tenants typically aren&apos;t responsible for major systems (HVAC, plumbing, appliances).",
              },
              {
                t: "Pet, guest, and roommate rules",
                d: "Hidden fees, pet deposits (often non-refundable), and overnight-guest restrictions can become real problems.",
              },
            ].map((step, i) => (
              <li key={step.t} className="rounded-2xl border border-border bg-card p-5 shadow-card">
                <div className="flex items-start gap-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-glow text-sm font-bold text-primary-foreground">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-semibold">{step.t}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{step.d}</p>
                    {step.href ? (
                      <Link
                        to={step.href as any}
                        className="mt-2 inline-block text-sm font-semibold text-primary-glow hover:underline"
                      >
                        {step.hrefLabel}
                      </Link>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="border-y border-border bg-surface/30 py-12">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <h2 className="flex items-center gap-2 text-2xl font-bold">
              <AlertTriangle className="h-6 w-6 text-destructive" />
              Red flags that mean don&apos;t sign yet
            </h2>
            <ul className="mt-5 grid gap-3 sm:grid-cols-2">
              {[
                "Deposit greater than 2 months' rent",
                "No grace period for late rent",
                "'Enter at any time' clause",
                "Auto-renewal with no rent cap",
                "Owe all remaining rent if you leave early",
                "Non-refundable cleaning fees",
                "Vague 'as-is' condition with no inspection",
                "Pet deposit + monthly pet rent + non-refundable pet fee",
              ].map((r) => (
                <li
                  key={r}
                  className="flex gap-2 rounded-xl border border-destructive/20 bg-destructive/5 p-3 text-sm"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-destructive" />
                  {r}
                </li>
              ))}
            </ul>

            <h2 className="mt-10 flex items-center gap-2 text-2xl font-bold">
              <CheckCircle2 className="h-6 w-6 text-success" />
              Green flags that mean you&apos;re probably safe
            </h2>
            <ul className="mt-5 grid gap-3 sm:grid-cols-2">
              {[
                "Deposit ≤ 1 month's rent, returned within 30 days",
                "5-day grace period and a capped late fee",
                "24–48 hours written entry notice",
                "Auto-rolls to month-to-month, no rent jump",
                "Flat early termination fee",
                "Move-in inspection report with photos",
                "Landlord pays for major repairs and pest control",
                "Clear definition of 'normal wear and tear'",
              ].map((r) => (
                <li
                  key={r}
                  className="flex gap-2 rounded-xl border border-success/20 bg-success/5 p-3 text-sm"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-success" />
                  {r}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
          <h2 className="flex items-center gap-2 text-2xl font-bold">
            <Lightbulb className="h-6 w-6 text-primary-glow" />
            What to negotiate (yes, you can)
          </h2>
          <ul className="mt-5 space-y-2 text-base leading-relaxed">
            {[
              "Lower the deposit to one month's rent.",
              "Add a job-relocation or military exception to early termination.",
              "Cap renewal rent increases at 5% (or local CPI).",
              "Replace 'sole discretion' with 'reasonable, itemized'.",
              "Add a written move-in condition report with dated photos.",
            ].map((t) => (
              <li key={t} className="flex gap-2">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-glow" />
                {t}
              </li>
            ))}
          </ul>
        </section>

        <section className="border-t border-border bg-gradient-glow py-14">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <ShieldCheck className="mx-auto h-10 w-10 text-primary-glow" />
              <h2 className="mt-3 text-3xl font-bold">
                Don&apos;t read it alone — decode it in 30 seconds
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
                Upload your lease and legaldecoder will flag every clause that&apos;s worse than
                industry standard, in plain English.
              </p>
            </div>
            <div className="mt-8">
              <ContractAnalyzer />
            </div>
            <div className="mt-6 text-center">
              <Link
                to="/tools/$slug"
                params={{ slug: "lease-decoder" }}
                className="inline-flex items-center gap-1 text-sm font-semibold text-primary-glow hover:underline"
              >
                Or open the dedicated Lease Decoder <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="mt-4 w-full">
            {FAQ.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`}>
                <AccordionTrigger>{f.q}</AccordionTrigger>
                <AccordionContent>{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <p className="mt-10 text-center text-xs uppercase tracking-wider text-muted-foreground">
            Not legal advice. For informational purposes only.
          </p>
        </section>

        <Footer />
      </main>
    </div>
  );
}
